// Flyer Header and Footer Components with Configuration
import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { AlignLeft, AlignCenter, AlignRight, Bold, Italic, Type, ALargeSmall, Sparkles } from 'lucide-react';
import {
  StoreConfig,
  FlyerTheme,
  HeaderTitleConfig,
  TextStyle,
  TitlePreset,
  TITLE_PRESETS,
  FONT_SIZE_OPTIONS,
  TEXT_COLOR_OPTIONS,
  getTextStyleClasses,
  DEFAULT_HEADER_TITLE_CONFIG,
} from './flyer-themes';

// ============ STORE CONFIG DIALOG ============

interface StoreConfigDialogProps {
  config: StoreConfig;
  onConfigChange: (config: StoreConfig) => void;
  isOpen: boolean;
  onClose: () => void;
}

export function StoreConfigDialog({
  config,
  onConfigChange,
  isOpen,
  onClose,
}: StoreConfigDialogProps) {
  const [form, setForm] = useState<StoreConfig>({ ...config });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfigChange(form);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Store Information</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Store Name */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Store Name</Label>
              <Input
                value={form.storeName}
                onChange={(e) => setForm({ ...form, storeName: e.target.value })}
                placeholder="Store Name"
              />
            </div>
            <div>
              <Label>Store Name (Chinese)</Label>
              <Input
                value={form.storeNameChinese}
                onChange={(e) => setForm({ ...form, storeNameChinese: e.target.value })}
                placeholder="中文店名"
              />
            </div>
          </div>

          {/* Tagline */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Tagline</Label>
              <Input
                value={form.tagline}
                onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                placeholder="Your tagline"
              />
            </div>
            <div>
              <Label>Tagline (Chinese)</Label>
              <Input
                value={form.taglineChinese}
                onChange={(e) => setForm({ ...form, taglineChinese: e.target.value })}
                placeholder="中文標語"
              />
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <Label>Address</Label>
            <Input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Store address"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Hours</Label>
              <Input
                value={form.hours}
                onChange={(e) => setForm({ ...form, hours: e.target.value })}
                placeholder="Open hours"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Phone number"
              />
            </div>
          </div>

          <div>
            <Label>Website</Label>
            <Input
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="www.example.com"
            />
          </div>

          <div>
            <Label>Logo URL</Label>
            <Input
              value={form.logoUrl}
              onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
              placeholder="/images/logo.png"
            />
            {form.logoUrl && (
              <div className="mt-2 p-2 bg-gray-100 rounded">
                <img src={form.logoUrl} alt="Logo preview" className="h-10 w-auto" />
              </div>
            )}
          </div>

          {/* QR Code Settings */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-semibold">QR Code on Flyer</Label>
              <Switch
                checked={form.showQrCode || false}
                onCheckedChange={(checked) => setForm({ ...form, showQrCode: checked })}
              />
            </div>
            {form.showQrCode && (
              <div className="space-y-3">
                <div>
                  <Label>QR Code URL</Label>
                  <Input
                    value={form.qrCodeUrl || ''}
                    onChange={(e) => setForm({ ...form, qrCodeUrl: e.target.value })}
                    placeholder="https://www.yourwebsite.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This URL will be encoded in the QR code shown on the flyer footer
                  </p>
                </div>
                {form.qrCodeUrl && (
                  <div className="flex items-center gap-3 p-2 bg-gray-100 rounded">
                    <QRCodeSVG value={form.qrCodeUrl} size={48} />
                    <span className="text-xs text-gray-500">Preview</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Banner Image Settings */}
          <div className="border-t pt-4">
            <Label className="text-sm font-semibold mb-3 block">Banner Image</Label>
            <div className="space-y-3">
              <div>
                <Label>Banner Image URL</Label>
                <Input
                  value={form.bannerImageUrl || ''}
                  onChange={(e) => setForm({ ...form, bannerImageUrl: e.target.value })}
                  placeholder="https://example.com/banner.jpg"
                />
              </div>
              {form.bannerImageUrl && (
                <>
                  <div>
                    <Label>Position</Label>
                    <Select
                      value={form.bannerPosition || 'header'}
                      onValueChange={(v) => setForm({ ...form, bannerPosition: v as 'header' | 'footer' | 'both' })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="header">Header only</SelectItem>
                        <SelectItem value="footer">Footer only</SelectItem>
                        <SelectItem value="both">Both header & footer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Opacity ({form.bannerOpacity || 30}%)</Label>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      value={form.bannerOpacity || 30}
                      onChange={(e) => setForm({ ...form, bannerOpacity: parseInt(e.target.value) })}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                  <div className="p-2 bg-gray-100 rounded relative overflow-hidden h-16">
                    <img
                      src={form.bannerImageUrl}
                      alt="Banner preview"
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ opacity: (form.bannerOpacity || 30) / 100 }}
                    />
                    <span className="relative z-10 text-xs text-gray-600">Preview</span>
                  </div>
                </>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============ HEADER TITLE CONFIG DIALOG ============

interface HeaderTitleConfigDialogProps {
  config: HeaderTitleConfig;
  onConfigChange: (config: HeaderTitleConfig) => void;
  isOpen: boolean;
  onClose: () => void;
}

const LETTER_SPACING_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'wide', label: 'Wide' },
  { value: 'wider', label: 'Wider' },
];

const TEXT_TRANSFORM_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'uppercase', label: 'UPPER' },
  { value: 'capitalize', label: 'Capitalize' },
];

function TextStyleEditor({
  label,
  style,
  onChange,
}: {
  label: string;
  style: TextStyle;
  onChange: (style: TextStyle) => void;
}) {
  return (
    <div className="space-y-2">
      <Label className="text-xs text-gray-500">{label} Styling</Label>
      <div className="flex items-center gap-2 flex-wrap">
        {/* Font Size */}
        <Select
          value={style.fontSize}
          onValueChange={(v) => onChange({ ...style, fontSize: v as TextStyle['fontSize'] })}
        >
          <SelectTrigger className="w-28 h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FONT_SIZE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Color */}
        <Select
          value={style.color}
          onValueChange={(v) => onChange({ ...style, color: v })}
        >
          <SelectTrigger className="w-32 h-8">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded ${TEXT_COLOR_OPTIONS.find(c => c.value === style.color)?.preview || 'bg-gray-400'}`} />
              <SelectValue />
            </div>
          </SelectTrigger>
          <SelectContent>
            {TEXT_COLOR_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded ${opt.preview}`} />
                  {opt.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Alignment */}
        <div className="flex border rounded">
          <button
            type="button"
            onClick={() => onChange({ ...style, align: 'left' })}
            className={`p-1.5 ${style.align === 'left' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
          >
            <AlignLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => onChange({ ...style, align: 'center' })}
            className={`p-1.5 ${style.align === 'center' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
          >
            <AlignCenter className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => onChange({ ...style, align: 'right' })}
            className={`p-1.5 ${style.align === 'right' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
          >
            <AlignRight className="w-4 h-4" />
          </button>
        </div>

        {/* Bold */}
        <button
          type="button"
          onClick={() => onChange({ ...style, bold: !style.bold })}
          className={`p-1.5 border rounded ${style.bold ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </button>

        {/* Italic */}
        <button
          type="button"
          onClick={() => onChange({ ...style, italic: !style.italic })}
          className={`p-1.5 border rounded ${style.italic ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </button>

        {/* Text Shadow */}
        <button
          type="button"
          onClick={() => onChange({ ...style, textShadow: !style.textShadow })}
          className={`p-1.5 border rounded ${style.textShadow ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
          title="Text Shadow"
        >
          <Sparkles className="w-4 h-4" />
        </button>
      </div>

      {/* Second row for additional options */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Letter Spacing */}
        <div className="flex items-center gap-1">
          <ALargeSmall className="w-4 h-4 text-gray-500" />
          <Select
            value={style.letterSpacing || 'normal'}
            onValueChange={(v) => onChange({ ...style, letterSpacing: v as TextStyle['letterSpacing'] })}
          >
            <SelectTrigger className="w-24 h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LETTER_SPACING_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Text Transform */}
        <div className="flex items-center gap-1">
          <Type className="w-4 h-4 text-gray-500" />
          <Select
            value={style.textTransform || 'none'}
            onValueChange={(v) => onChange({ ...style, textTransform: v as TextStyle['textTransform'] })}
          >
            <SelectTrigger className="w-28 h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEXT_TRANSFORM_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

export function HeaderTitleConfigDialog({
  config,
  onConfigChange,
  isOpen,
  onClose,
}: HeaderTitleConfigDialogProps) {
  const [form, setForm] = useState<HeaderTitleConfig>({ ...config });
  const [selectedPreset, setSelectedPreset] = useState<string>('custom');

  // Sync form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setForm({ ...config });
      // Try to match a preset
      const preset = TITLE_PRESETS.find(p =>
        p.title === config.title && p.titleChinese === config.titleChinese
      );
      setSelectedPreset(preset?.id || 'custom');
    }
  }, [isOpen, config]);

  const handlePresetChange = (presetId: string) => {
    setSelectedPreset(presetId);
    const preset = TITLE_PRESETS.find(p => p.id === presetId);
    if (preset && presetId !== 'custom') {
      setForm(prev => ({
        ...prev,
        title: preset.title,
        titleChinese: preset.titleChinese,
        subtitle: preset.subtitle,
        subtitleChinese: preset.subtitleChinese,
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfigChange(form);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Header Title</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Preset Selector */}
          <div>
            <Label>Quick Preset</Label>
            <Select value={selectedPreset} onValueChange={handlePresetChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a preset..." />
              </SelectTrigger>
              <SelectContent>
                {TITLE_PRESETS.map((preset) => (
                  <SelectItem key={preset.id} value={preset.id}>
                    {preset.name} {preset.titleChinese && `/ ${preset.titleChinese}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Divider */}
          <div className="border-t pt-4">
            <Label className="text-sm font-semibold">Main Title</Label>
          </div>

          {/* Main Title */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-gray-500">English</Label>
              <Input
                value={form.title}
                onChange={(e) => {
                  setForm({ ...form, title: e.target.value.toUpperCase() });
                  setSelectedPreset('custom');
                }}
                placeholder="WEEKLY SPECIALS"
                className="font-bold"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Chinese (optional)</Label>
              <Input
                value={form.titleChinese || ''}
                onChange={(e) => {
                  setForm({ ...form, titleChinese: e.target.value });
                  setSelectedPreset('custom');
                }}
                placeholder="每週特價"
              />
            </div>
          </div>

          {/* Title Style */}
          <TextStyleEditor
            label="Title"
            style={form.titleStyle}
            onChange={(titleStyle) => setForm({ ...form, titleStyle })}
          />

          {/* Divider */}
          <div className="border-t pt-4">
            <Label className="text-sm font-semibold">Subtitle (optional)</Label>
          </div>

          {/* Subtitle */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-gray-500">English</Label>
              <Input
                value={form.subtitle || ''}
                onChange={(e) => {
                  setForm({ ...form, subtitle: e.target.value });
                  setSelectedPreset('custom');
                }}
                placeholder="Fresh Deals Every Week!"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Chinese (optional)</Label>
              <Input
                value={form.subtitleChinese || ''}
                onChange={(e) => {
                  setForm({ ...form, subtitleChinese: e.target.value });
                  setSelectedPreset('custom');
                }}
                placeholder="每週新鮮優惠！"
              />
            </div>
          </div>

          {/* Subtitle Style */}
          <TextStyleEditor
            label="Subtitle"
            style={form.subtitleStyle}
            onChange={(subtitleStyle) => setForm({ ...form, subtitleStyle })}
          />

          {/* Preview */}
          <div className="border rounded-lg p-4 bg-gradient-to-r from-red-700 via-red-600 to-red-700">
            <div className="text-right">
              <div className={getTextStyleClasses(form.titleStyle)}>
                {form.title || 'TITLE'}
                {form.titleChinese && <span className="ml-2 opacity-80">{form.titleChinese}</span>}
              </div>
              {(form.subtitle || form.subtitleChinese) && (
                <div className={getTextStyleClasses(form.subtitleStyle)}>
                  {form.subtitle}
                  {form.subtitleChinese && <span className="ml-2">{form.subtitleChinese}</span>}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Apply</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============ HEADER COMPONENTS ============

interface FlyerHeaderProps {
  theme: FlyerTheme;
  storeConfig: StoreConfig;
  titleConfig: HeaderTitleConfig;
  dateRange: string;
}

// For backwards compatibility
interface FlyerHeaderPropsLegacy {
  theme: FlyerTheme;
  storeConfig: StoreConfig;
  flyerTitle: string;
  dateRange: string;
}

function TitleDisplay({ config }: { config: HeaderTitleConfig }) {
  return (
    <>
      <div className={getTextStyleClasses(config.titleStyle)}>
        {config.title}
        {config.titleChinese && <span className="ml-1 opacity-90">{config.titleChinese}</span>}
      </div>
      {(config.subtitle || config.subtitleChinese) && (
        <div className={getTextStyleClasses(config.subtitleStyle)}>
          {config.subtitle}
          {config.subtitleChinese && <span className="ml-1">{config.subtitleChinese}</span>}
        </div>
      )}
    </>
  );
}

// Banner overlay component
function BannerOverlay({ storeConfig, position }: { storeConfig: StoreConfig; position: 'header' | 'footer' }) {
  if (!storeConfig.bannerImageUrl) return null;
  const showBanner = storeConfig.bannerPosition === 'both' || storeConfig.bannerPosition === position;
  if (!showBanner) return null;

  return (
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{
        backgroundImage: `url(${storeConfig.bannerImageUrl})`,
        opacity: (storeConfig.bannerOpacity || 30) / 100,
      }}
    />
  );
}

export function FlyerHeaderClassic({ theme, storeConfig, titleConfig, dateRange }: FlyerHeaderProps) {
  return (
    <div className={`${theme.colorScheme.headerBg} text-white px-4 py-3 relative overflow-hidden`}>
      <BannerOverlay storeConfig={storeConfig} position="header" />
      <div className="flex items-center justify-between relative z-10">
        <div className="flex items-center gap-4">
          {storeConfig.logoUrl && (
            <img src={storeConfig.logoUrl} alt={storeConfig.storeName} className="h-12 w-auto" />
          )}
          <div>
            <h1 className="text-2xl font-black tracking-tight">{storeConfig.storeName}</h1>
            <p className="text-sm opacity-90">{storeConfig.storeNameChinese} - {storeConfig.tagline}</p>
          </div>
        </div>
        <div className="text-right">
          <div className={`${theme.colorScheme.accent} text-red-800 px-3 py-1 rounded font-bold mb-1`}>
            <TitleDisplay config={titleConfig} />
          </div>
          <p className="text-xs opacity-90">{dateRange}</p>
        </div>
      </div>
    </div>
  );
}

export function FlyerHeaderFestive({ theme, storeConfig, titleConfig, dateRange }: FlyerHeaderProps) {
  return (
    <div className={`${theme.colorScheme.headerBg} text-white relative overflow-hidden`}>
      <BannerOverlay storeConfig={storeConfig} position="header" />
      {/* Decorative pattern */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23FFD700' fill-opacity='1' fill-rule='evenodd'%3E%3Ccircle cx='20' cy='20' r='3'/%3E%3C/g%3E%3C/svg%3E")`
      }} />

      <div className="relative px-4 py-4 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {storeConfig.logoUrl && (
              <div className="bg-white p-2 rounded-lg shadow-lg">
                <img src={storeConfig.logoUrl} alt={storeConfig.storeName} className="h-14 w-auto" />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-black tracking-tight drop-shadow-lg">{storeConfig.storeName}</h1>
              <p className="text-base opacity-90 font-medium">{storeConfig.storeNameChinese}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="bg-yellow-400 text-red-800 px-4 py-2 rounded-lg shadow-lg transform -rotate-2">
              <TitleDisplay config={titleConfig} />
            </div>
            <p className="text-sm font-medium mt-1">{dateRange}</p>
          </div>
        </div>
        <p className="text-center text-yellow-300 font-bold mt-2">{storeConfig.tagline} * {storeConfig.taglineChinese}</p>
      </div>

      {/* Bottom decorative border */}
      <div className="h-2 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400 relative z-10" />
    </div>
  );
}

export function FlyerHeaderMinimal({ theme, storeConfig, titleConfig, dateRange }: FlyerHeaderProps) {
  return (
    <div className={`${theme.colorScheme.headerBg} text-white px-4 py-2 relative overflow-hidden`}>
      <BannerOverlay storeConfig={storeConfig} position="header" />
      <div className="flex items-center justify-between relative z-10">
        <div className="flex items-center gap-3">
          {storeConfig.logoUrl && (
            <img src={storeConfig.logoUrl} alt={storeConfig.storeName} className="h-8 w-auto" />
          )}
          <h1 className="text-xl font-bold">{storeConfig.storeName}</h1>
        </div>
        <div className="flex items-center gap-4">
          <span className={`${theme.colorScheme.accent} text-gray-800 px-2 py-0.5 rounded`}>
            <TitleDisplay config={titleConfig} />
          </span>
          <span className="text-sm">{dateRange}</span>
        </div>
      </div>
    </div>
  );
}

// ============ FOOTER COMPONENTS ============

interface FlyerFooterProps {
  theme: FlyerTheme;
  storeConfig: StoreConfig;
}

export function FlyerFooterFull({ theme, storeConfig }: FlyerFooterProps) {
  return (
    <div className={`${theme.colorScheme.footerBg} text-white px-4 py-2 relative overflow-hidden`}>
      <BannerOverlay storeConfig={storeConfig} position="footer" />
      <div className="flex items-center justify-between text-xs relative z-10">
        <div className="flex items-center gap-4">
          <span>{storeConfig.address}</span>
          <span>{storeConfig.hours}</span>
          <span>{storeConfig.phone}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <span className="block">{storeConfig.website}</span>
            <span className="opacity-75">Prices valid while supplies last</span>
          </div>
          {storeConfig.showQrCode && storeConfig.qrCodeUrl && (
            <div className="bg-white p-1 rounded">
              <QRCodeSVG value={storeConfig.qrCodeUrl} size={40} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function FlyerFooterCompact({ theme, storeConfig }: FlyerFooterProps) {
  return (
    <div className={`${theme.colorScheme.footerBg} text-white px-4 py-1 relative overflow-hidden`}>
      <BannerOverlay storeConfig={storeConfig} position="footer" />
      <div className="flex items-center justify-center gap-4 text-xs relative z-10">
        <span>{storeConfig.address}</span>
        <span>*</span>
        <span>{storeConfig.hours}</span>
        <span>*</span>
        <span>{storeConfig.website}</span>
        {storeConfig.showQrCode && storeConfig.qrCodeUrl && (
          <div className="bg-white p-0.5 rounded ml-2">
            <QRCodeSVG value={storeConfig.qrCodeUrl} size={28} />
          </div>
        )}
      </div>
    </div>
  );
}

export function FlyerFooterMinimal({ theme, storeConfig }: FlyerFooterProps) {
  return (
    <div className={`${theme.colorScheme.footerBg} text-white px-4 py-1 relative overflow-hidden`}>
      <BannerOverlay storeConfig={storeConfig} position="footer" />
      <div className="flex items-center justify-center gap-2 text-xs relative z-10">
        <span>{storeConfig.website} * {storeConfig.phone}</span>
        {storeConfig.showQrCode && storeConfig.qrCodeUrl && (
          <div className="bg-white p-0.5 rounded">
            <QRCodeSVG value={storeConfig.qrCodeUrl} size={24} />
          </div>
        )}
      </div>
    </div>
  );
}

// ============ HEADER/FOOTER SELECTOR ============

export function renderHeader(
  style: FlyerTheme['headerStyle'],
  props: FlyerHeaderProps
) {
  switch (style) {
    case 'festive':
      return <FlyerHeaderFestive {...props} />;
    case 'minimal':
      return <FlyerHeaderMinimal {...props} />;
    case 'classic':
    default:
      return <FlyerHeaderClassic {...props} />;
  }
}

export function renderFooter(
  style: FlyerTheme['footerStyle'],
  props: FlyerFooterProps
) {
  switch (style) {
    case 'compact':
      return <FlyerFooterCompact {...props} />;
    case 'minimal':
      return <FlyerFooterMinimal {...props} />;
    case 'full':
    default:
      return <FlyerFooterFull {...props} />;
  }
}
